import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ds';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { contentApi } from '../../api/content';
import { progressApi } from '../../api/progress';
import type { ExerciseSection, ExerciseSetQuestionView } from '../../api/content.types';

const card: React.CSSProperties = { background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)' };

const SECTION_META: Record<ExerciseSection, { jp: string; vi: string }> = {
  VOCAB: { jp: '文字・語彙', vi: 'Từ vựng' },
  GRAMMAR: { jp: '文法', vi: 'Ngữ pháp' },
  READING: { jp: '読解', vi: 'Đọc hiểu' },
};

/**
 * Loại layout hiển thị — suy từ VỊ TRÍ câu trong bộ đề (order 1-12), vì cấu
 * trúc mondai CỐ ĐỊNH với mọi bộ đề thật (xem exerciseSets.ts ở seed).
 */
type ExerciseQuestionType = 'read' | 'write' | 'blank' | 'para' | 'order' | 'passage';
const TYPE_BY_ORDER: ExerciseQuestionType[] = ['read', 'read', 'write', 'blank', 'para', 'blank', 'blank', 'blank', 'order', 'order', 'passage', 'passage'];

/** Tách `<u>text</u>` thành đoạn gạch chân trong câu hỏi dạng read/write/para. */
function markUnderline(text: string) {
  return text.split(/<u>|<\/u>/).map((seg, i) =>
    i % 2 === 1 ? (
      <u key={i} style={{ textUnderlineOffset: 5, textDecorationThickness: 2 }}>
        {seg}
      </u>
    ) : (
      <span key={i}>{seg}</span>
    ),
  );
}

/**
 * Tách câu 文の組み立て quanh 4 ô ＿＿＿/★＿＿ thành phần chữ trước/sau ô, cộng
 * dòng hội thoại còn lại (nếu có). 4 ô KHÔNG LUÔN ở dòng đầu — ví dụ A「…」 là
 * câu đủ, B「＿＿＿…」 mới có ô — nên phải tìm đúng dòng chứa ô thay vì giả định
 * luôn là dòng đầu (bug cũ: dòng có ô ở vị trí thứ 2 bị hiện chữ thô, không
 * tách thành ô bấm được).
 */
function splitOrderPrompt(prompt: string): { before: string | null; prefix: string; suffix: string; after: string | null } {
  const lines = prompt.split('\n').map((l) => l.trim());
  const blankLineIndex = Math.max(
    0,
    lines.findIndex((l) => /＿＿＿|★＿＿/.test(l)),
  );
  const blankLine = lines[blankLineIndex];
  const tokens = [...blankLine.matchAll(/＿＿＿|★＿＿/g)];
  const firstStart = tokens[0]?.index ?? blankLine.length;
  const last = tokens[tokens.length - 1];
  const lastEnd = last ? last.index! + last[0].length : blankLine.length;
  return {
    before: lines.slice(0, blankLineIndex).join(' ') || null,
    prefix: blankLine.slice(0, firstStart).trim(),
    suffix: blankLine.slice(lastEnd).trim(),
    after: lines.slice(blankLineIndex + 1).join(' ') || null,
  };
}

function OptionButton({ index, label, selected, big, onClick }: { index: number; label: string; selected: boolean; big?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 56,
        padding: '10px 12px',
        textAlign: 'left',
        borderRadius: 'var(--radius-control)',
        border: `2px solid ${selected ? 'var(--cta-default)' : 'var(--border-default)'}`,
        background: selected ? 'var(--surface-tint)' : 'var(--surface-page)',
        fontFamily: 'var(--font-jp, sans-serif)',
        fontSize: big ? 22 : 17,
        color: 'var(--ink)',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: '50%',
          background: selected ? 'var(--cta-default)' : 'var(--surface-tint)',
          color: selected ? '#fff' : 'var(--text-secondary)',
          fontSize: 13,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-vi)',
        }}
      >
        {index + 1}
      </span>
      {label}
    </button>
  );
}

function QuestionBody({
  q,
  type,
  passage,
  picked,
  orderSeq,
  onPick,
  onOrderTap,
  onOrderRemove,
}: {
  q: ExerciseSetQuestionView;
  type: ExerciseQuestionType;
  passage: string;
  picked: number | undefined;
  orderSeq: number[];
  onPick: (v: number) => void;
  onOrderTap: (optionIndex1based: number) => void;
  onOrderRemove: (slot: number) => void;
}) {
  if (type === 'passage') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...card, background: 'var(--surface-tint)', padding: 14, fontFamily: 'var(--font-jp, sans-serif)', fontSize: 15, lineHeight: 1.8, color: 'var(--ink)', whiteSpace: 'pre-line' }}>
          {passage}
        </div>
        <div style={{ fontSize: 18, lineHeight: 1.6, fontWeight: 600, color: 'var(--ink)' }}>{q.prompt}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {q.options.map((o, i) => (
            <OptionButton key={i} index={i} label={o} selected={picked === i + 1} onClick={() => onPick(i + 1)} />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'order') {
    const slots = [0, 1, 2, 3];
    const { before, prefix, suffix, after } = splitOrderPrompt(q.prompt);
    return (
      <div>
        <div style={{ ...card, padding: '16px 14px', marginBottom: 14, fontFamily: 'var(--font-jp, sans-serif)', fontSize: 16, lineHeight: 1.6, color: 'var(--ink)' }}>
          {before && <div style={{ fontWeight: 600, marginBottom: 10 }}>{before}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{prefix}</span>
            {slots.map((k) => {
              const v = orderSeq[k];
              const isStar = k === 2;
              return (
                <button
                  key={k}
                  onClick={() => v && onOrderRemove(k)}
                  style={{
                    minWidth: 52,
                    height: 44,
                    padding: '0 8px',
                    borderRadius: 8,
                    border: `2px ${v ? 'solid' : 'dashed'} ${isStar ? 'var(--cta-default)' : 'var(--border-default)'}`,
                    background: v ? 'var(--surface-tint)' : 'var(--surface-page)',
                    color: 'var(--ink)',
                    fontFamily: 'var(--font-jp, sans-serif)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: v ? 'pointer' : 'default',
                  }}
                >
                  {v ? q.options[v - 1] : isStar ? <span style={{ color: 'var(--cta-default)', fontSize: 18 }}>★</span> : ''}
                </button>
              );
            })}
            <span style={{ fontWeight: 600 }}>{suffix}</span>
          </div>
          {after && <div style={{ fontWeight: 600, marginTop: 10 }}>{after}</div>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Chạm để xếp vào ô trống theo thứ tự · chạm ô để bỏ ra</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {q.options.map((t, i) => {
            const used = orderSeq.includes(i + 1);
            return (
              <button
                key={i}
                disabled={used}
                onClick={() => orderSeq.length < 4 && onOrderTap(i + 1)}
                style={{
                  minHeight: 44,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: '1px solid var(--border-default)',
                  background: used ? 'var(--surface-tint)' : 'var(--surface-page)',
                  color: used ? 'transparent' : 'var(--ink)',
                  fontFamily: 'var(--font-jp, sans-serif)',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: used ? 'default' : 'pointer',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const useGrid = (type === 'read' || type === 'write' || type === 'blank') && q.options.every((o) => o.length <= 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {type === 'blank' && (
        <div style={{ ...card, padding: '18px 16px', fontFamily: 'var(--font-jp, sans-serif)', fontSize: 19, fontWeight: 600, lineHeight: 2, color: 'var(--ink)', whiteSpace: 'pre-line' }}>
          {q.prompt.split('（　　）').map((t, i, arr) => (
            <span key={i}>
              {t}
              {i < arr.length - 1 && (
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 64,
                    margin: '0 4px',
                    padding: '0 8px',
                    textAlign: 'center',
                    borderBottom: '3px solid var(--cta-default)',
                    background: picked ? 'var(--surface-tint)' : 'transparent',
                    borderRadius: 6,
                    color: 'var(--cta-default)',
                  }}
                >
                  {picked ? q.options[picked - 1] : ' '}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {(type === 'para' || type === 'read' || type === 'write') && (
        <div style={type === 'para' ? { ...card, background: 'var(--surface-tint)', padding: '16px 18px', fontFamily: 'var(--font-jp, sans-serif)', fontSize: 18, fontWeight: 600, lineHeight: 1.7, color: 'var(--ink)' } : { ...card, padding: '18px 16px', fontFamily: 'var(--font-jp, sans-serif)', fontSize: 19, fontWeight: 600, lineHeight: 2, color: 'var(--ink)' }}>
          {markUnderline(q.prompt)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: useGrid ? '1fr 1fr' : '1fr', gap: 10 }}>
        {q.options.map((o, i) => (
          <OptionButton key={i} index={i} label={o} selected={picked === i + 1} big={type === 'write'} onClick={() => onPick(i + 1)} />
        ))}
      </div>
    </div>
  );
}

export interface ExerciseQuizScreenProps {
  unitId: string;
  baiNumber: number;
  setNumber: number;
  lo: number;
  hi: number;
  onBack: () => void;
  onDone: () => void;
}

export function ExerciseQuizScreen({ unitId, baiNumber, setNumber, lo, hi, onBack, onDone }: ExerciseQuizScreenProps) {
  const exerciseSet = useAsync(() => contentApi.getExerciseSet(unitId, setNumber), [unitId, setNumber]);

  const [step, setStep] = useState(lo);
  const [ans, setAns] = useState<Record<number, number>>({});
  const [ord, setOrd] = useState<Record<number, number[]>>({});

  const pick = (i: number, v: number) => setAns((a) => ({ ...a, [i]: v }));
  const setOrder = (i: number, arr: number[]) => {
    setOrd((o) => ({ ...o, [i]: arr }));
    if (arr.length === 4) pick(i, arr[2]);
    else
      setAns((a) => {
        const next = { ...a };
        delete next[i];
        return next;
      });
  };
  // Chấm & lưu tiến độ ngay khi vào màn Kết quả — CHỈ 1 lần/lượt làm (ref chặn
  // double-submit khi re-render), "Làm lại" reset ref để lượt sau nộp lại được.
  // Tiến độ node "Bài tập" ở Lộ trình tự cập nhật ở lần fetch getProgress tiếp theo.
  const submittedRef = useRef(false);

  // Mốc bắt đầu lượt làm — gửi kèm khi nộp để tab "Lịch sử" hiện thời gian làm bài.
  // Đặt khi đề tải xong (không tính thời gian chờ mạng) và đặt lại khi "Làm lại".
  const startedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (exerciseSet.data && startedAtRef.current === null) startedAtRef.current = Date.now();
  }, [exerciseSet.data]);

  const restart = () => {
    setAns({});
    setOrd({});
    setStep(lo);
    submittedRef.current = false;
    startedAtRef.current = Date.now();
  };

  useEffect(() => {
    if (step < hi || submittedRef.current || !exerciseSet.data) return;
    submittedRef.current = true;
    const answers = exerciseSet.data.questions
      .map((q, i) => ({ order: q.order, picked: ans[i], inRange: i >= lo && i < hi }))
      .filter((a) => a.inRange && a.picked != null)
      .map((a) => ({ order: a.order, picked: a.picked! }));
    if (answers.length === 0) return;
    const durationSeconds = startedAtRef.current === null ? undefined : Math.round((Date.now() - startedAtRef.current) / 1000);
    progressApi.submitExerciseAnswers(exerciseSet.data.id, answers, durationSeconds).catch(() => {
      // Lỗi mạng ở đây không đáng chặn UI kết quả — người dùng đã thấy điểm
      // ngay trên máy, chỉ tiến độ ở Lộ trình chưa cập nhật lần này.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, hi, exerciseSet.data]);

  if (exerciseSet.loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LoadingState />
      </div>
    );
  }
  if (exerciseSet.error || !exerciseSet.data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ErrorState message="Không tải được đề bài tập." onRetry={onBack} />
      </div>
    );
  }
  const questions = exerciseSet.data.questions;
  const passage = exerciseSet.data.passage;

  if (step >= hi) {
    const inRange = (i: number) => i >= lo && i < hi;
    const total = hi - lo;
    const score = questions.filter((q, i) => inRange(i) && ans[i] === q.answer).length;
    const verdict = score >= total * 0.85 ? 'Rất tốt! Bạn nắm chắc Bài này.' : score >= total * 0.6 ? 'Khá tốt — xem lại các câu sai bên dưới.' : 'Hãy ôn lại rồi làm lại nhé.';
    const sectionsInRange = (['VOCAB', 'GRAMMAR', 'READING'] as const).filter((s) => questions.some((q, i) => inRange(i) && q.section === s));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-vi)' }}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border-default)' }}>
          <button onClick={onDone} aria-label="Đóng" style={{ width: 44, height: 44, margin: '-6px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ink)' }}>
            ‹
          </button>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Kết quả</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{`Bài tập · Unit ${baiNumber}`}</div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ ...card, padding: 20, textAlign: 'center', background: 'var(--surface-tint)' }}>
            <div style={{ fontSize: 40, fontWeight: 700, color: 'var(--ink)' }}>
              {score}
              <span style={{ fontSize: 20, color: 'var(--text-secondary)' }}>{`/${total}`}</span>
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 4 }}>{verdict}</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, sectionsInRange.length)},1fr)`, gap: 8, marginTop: 16 }}>
              {sectionsInRange.map((s) => {
                const idx = questions.map((q, i) => (q.section === s && inRange(i) ? i : -1)).filter((i) => i >= 0);
                const ok = idx.filter((i) => ans[i] === questions[i].answer).length;
                return (
                  <div key={s} style={{ background: 'var(--surface-page)', borderRadius: 12, padding: '10px 4px' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>{`${ok}/${idx.length}`}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{SECTION_META[s].vi}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '22px 0 10px' }}>Đáp án &amp; giải thích</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {questions.map((q, i) => {
              if (!inRange(i)) return null;
              const ok = ans[i] === q.answer;
              return (
                <div key={i} style={{ ...card, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: ok ? 'var(--success)' : 'var(--error)',
                        color: '#fff',
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {ok ? '✓' : '✕'}
                    </span>
                    {`Câu ${i + 1} · Đáp án ${q.answer}`}
                    {!ok && ans[i] ? <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>{` (bạn chọn ${ans[i]})`}</span> : null}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 6 }}>{q.explanation}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '16px 20px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Button style={{ width: '100%', justifyContent: 'center' }} onClick={onDone}>
            Về lộ trình
          </Button>
          <Button variant="ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={restart}>
            Làm lại
          </Button>
        </div>
      </div>
    );
  }

  const q = questions[step];
  const type = TYPE_BY_ORDER[q.order - 1];
  const sec = SECTION_META[q.section];
  const picked = ans[step];
  const progressPct = ((step - lo + 1) / (hi - lo)) * 100;
  const goBack = () => (step > lo ? setStep(step - 1) : onBack());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-vi)' }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border-default)' }}>
        <button onClick={goBack} aria-label="Quay lại" style={{ width: 44, height: 44, margin: '-6px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ink)' }}>
          ‹
        </button>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{`${sec.vi} · ${sec.jp}`}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{`Câu ${step - lo + 1}/${hi - lo}`}</div>
        </div>
      </div>
      <div style={{ height: 4, background: 'var(--border-default)' }}>
        <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--cta-default)', transition: 'width .25s' }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cta-default)' }}>{q.mondai}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 14px', lineHeight: 1.5 }}>{q.instruction}</div>
        <QuestionBody
          q={q}
          type={type}
          passage={passage}
          picked={picked}
          orderSeq={ord[step] ?? []}
          onPick={(v) => pick(step, v)}
          onOrderTap={(v) => setOrder(step, [...(ord[step] ?? []), v])}
          onOrderRemove={(slot) => setOrder(step, (ord[step] ?? []).filter((_, j) => j !== slot))}
        />
      </div>

      <div style={{ padding: '16px 20px 24px', borderTop: '1px solid var(--border-default)' }}>
        <Button style={{ width: '100%', justifyContent: 'center' }} disabled={!picked} onClick={() => setStep(step + 1)}>
          {step === hi - 1 ? 'Nộp bài' : 'Câu tiếp theo'}
        </Button>
      </div>
    </div>
  );
}
